import {
  Box,
  CardHeader,
  Card,
  Typography,
  Divider,
  useTheme
} from '@mui/material';
import { OrdersGrandSummary } from 'src/services/apiService/response-models/report';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface SalesByOrderTypeProps {
  salesByOrderType?: Record<string, OrdersGrandSummary>;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A569BD', '#F1948A', '#45B39D', '#F7DC6F'];

function CustomTooltip({ active, payload }: any) {
  const { t }: { t: any } = useTranslation();
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, boxShadow: 2 }}>
        <Typography variant="subtitle2">{data.name}</Typography>
        <Typography variant="body2">
          {t('Total')}: <b>${data.value?.toLocaleString()}</b>
        </Typography>
        <Typography variant="body2">
          {t('Net Total')}: <b>${data.netTotal?.toLocaleString()}</b>
        </Typography>
        <Typography variant="body2">
          {t('Orders')}: <b>{data.ordersCount}</b>
        </Typography>
      </Box>
    );
  }
  return null;
}

function SalesByOrderType({ salesByOrderType }: SalesByOrderTypeProps) {
  const { t }: { t: any } = useTranslation();
  const theme = useTheme();

  // Prepare data for PieChart
  const data = salesByOrderType
    ? Object.entries(salesByOrderType).map(([type, summary]) => ({
        name: t(type),
        value: summary.orderTotal || 0,
        ordersCount: summary.ordersCount,
        netTotal: summary.netTotal
      }))
    : [];

  return (
    <Card variant="outlined">
      <CardHeader
        sx={{ p: 3 }}
        disableTypography
        title={
          <Typography
            variant="h4"
            sx={{ fontSize: `${theme.typography.pxToRem(16)}` }}
          >
            {t('Sales By Order Type')}
          </Typography>
        }
      />
      <Divider />
      <Box sx={{ height: 320, display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ name, value }) => `${name}: $${value.toLocaleString()}`}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2">{t('No data available')}</Typography>
        )}
      </Box>
    </Card>
  );
}

export default SalesByOrderType;
